import { type AdminCompanyDetailDto, type AdminCompanyUserDto, IntelligencePersona } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Avatar } from '@components/shared/avatar.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { IntelligencePersonaSelect } from '@components/shared/intelligence-persona-select.component';
import { useAppForm } from '@components/ui/form/form';
import { FormErrorMessage } from '@components/ui/form/shared.component';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useAdminAddOrganizationMember,
  useAdminRemoveOrganizationMember,
  useAdminSetOrganizationMemberPersona,
} from '@queries/intel-organization.query';
import { routes } from '@routes';
import { createFormInvalidHandler } from '@utils/form.util';
import { ArrowRightIcon, CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminIntelMemberPicker } from './admin-intel-member-picker.component';
import { adminAddIntelMemberFormOptions, adminAddIntelMemberSchema } from './admin-organizations-form-options';

type AdminOrganizationMembersTileProps = {
  organization: AdminCompanyDetailDto;
};

// AZ-451: Intelligence orgs are managed by IntelligencePersona, not the generic UserCompanyRole. Each
// member row mirrors the innovator "Manage Team" tile — the persona dropdown updates the persona and
// its red "Remove" option removes the member; the navigate button (view in Admin Users) sits top-right.
// Adding a member happens inline (D10 follow-up): the "Add" affordance swaps the roster for a compact
// form with Discard/Save icon actions, so there is no nested modal.
export const AdminOrganizationMembersTile: FC<AdminOrganizationMembersTileProps> = (props) => {
  const { organization } = props;

  const { mutateAsync: setPersona } = useAdminSetOrganizationMemberPersona();
  const { mutateAsync: removeMember } = useAdminRemoveOrganizationMember();
  const { isPending: isAddingMember, mutateAsync: addMember } = useAdminAddOrganizationMember();

  const [isAdding, setIsAdding] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<AdminCompanyUserDto | null>(null);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);

  const members = organization.users ?? [];
  const isDeleted = Boolean(organization.deleted);

  const addForm = useAppForm({
    ...adminAddIntelMemberFormOptions,
    onSubmit: async ({ value }) => {
      if (!value.persona || !value.userId) return;

      try {
        await addMember({
          companyId: organization.id,
          data: { persona: value.persona, userId: value.userId },
        });
        successToast({ description: 'Member added successfully.' });
        addForm.reset();
        setIsAdding(false);
      } catch (error) {
        errorToast({ description: error instanceof Error ? error.message : 'Failed to add member.' });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: adminAddIntelMemberSchema,
    },
  });

  const handleStartAdd = (): void => setIsAdding(true);

  const handleCancelAdd = (): void => {
    addForm.reset();
    setIsAdding(false);
  };

  const handlePersonaChange = async (member: AdminCompanyUserDto, persona: IntelligencePersona): Promise<void> => {
    if (persona === member.persona) return;

    setUpdatingMemberId(member.userCompanyId);
    try {
      await setPersona({ companyId: organization.id, data: { persona }, userCompanyId: member.userCompanyId });
      successToast({ description: 'Member persona updated.' });
    } catch (error) {
      const message =
        error instanceof ApiErrorWrapper ? error.message : 'Unable to update persona. Please try again later.';
      errorToast({ description: message });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveConfirm = async (): Promise<void> => {
    if (!memberToRemove) return;
    await removeMember({ companyId: organization.id, userCompanyId: memberToRemove.userCompanyId });
    setMemberToRemove(null);
  };

  if (isAdding) {
    return (
      <div className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Add Member</h3>
          <div className="flex items-center gap-2">
            <IconButton
              ariaLabel="Discard"
              disabled={isAddingMember}
              icon={<XIcon />}
              onClick={handleCancelAdd}
              size="md"
              title="Discard"
              type="button"
              variant="secondary"
            />
            <IconButton
              ariaLabel="Save member"
              icon={<CheckIcon />}
              loading={isAddingMember}
              onClick={() => void addForm.handleSubmit()}
              size="md"
              title="Save"
              type="button"
              variant="primary"
            />
          </div>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void addForm.handleSubmit();
          }}
        >
          <addForm.AppForm>
            <div className="flex flex-col gap-4">
              <addForm.AppField name="userId">
                {(field) => {
                  const errorEntry = field.state.meta.errorMap?.onSubmit?.[0];
                  const message = typeof errorEntry === 'string' ? errorEntry : errorEntry?.message;
                  return (
                    <div className="flex flex-col gap-1.5">
                      <AdminIntelMemberPicker
                        companyId={organization.id}
                        hasError={Boolean(message)}
                        isRequired
                        label="User"
                        onChange={(userId) => field.handleChange(userId)}
                        value={field.state.value}
                      />
                      {message && <FormErrorMessage message={message} />}
                    </div>
                  );
                }}
              </addForm.AppField>

              <addForm.AppField name="persona">
                {(field) => {
                  const errorEntry = field.state.meta.errorMap?.onSubmit?.[0];
                  const message = typeof errorEntry === 'string' ? errorEntry : errorEntry?.message;
                  return (
                    <div className="flex flex-col gap-1.5">
                      <IntelligencePersonaSelect
                        className="w-full"
                        onPersonaChange={(persona) => field.handleChange(persona)}
                        persona={field.state.value}
                      />
                      {message && <FormErrorMessage message={message} />}
                    </div>
                  );
                }}
              </addForm.AppField>
            </div>
          </addForm.AppForm>
        </form>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-4">
        <EmptyStateContainer
          message={isDeleted ? undefined : 'Add Member'}
          onClick={isDeleted ? undefined : handleStartAdd}
          title="No Members"
        />
      </div>
    );
  }

  return (
    <div className="group/tile flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Members ({members.length})</h3>
        {!isDeleted && (
          <IconButton
            ariaLabel="Add member"
            className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover/tile:opacity-100"
            icon={<PlusIcon />}
            onClick={handleStartAdd}
            size="md"
            variant="secondary"
          />
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {members.map((member) => {
          const name = member.fullName || member.email;

          return (
            <div
              key={member.userCompanyId}
              className="group/member flex flex-col gap-3 rounded-lg border border-gray-100 p-4"
            >
              <div className="flex items-start gap-3">
                <Avatar
                  altText={name}
                  className="shrink-0"
                  imageUrl={member.avatarUrl ?? undefined}
                  initialText={name}
                  size="sm"
                />
                <div className="flex min-w-0 flex-1 flex-col pt-1">
                  <span className="break-words text-sm font-medium text-gray-900">{name}</span>
                  <span className="break-all text-xs text-gray-500">{member.email}</span>
                </div>
                <Link
                  className="shrink-0"
                  to={`${routes.adminUsers.path}?userId=${member.id}`}
                >
                  <IconButton
                    ariaLabel="View user in Admin Users"
                    className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover/member:opacity-100"
                    icon={<ArrowRightIcon />}
                    size="md"
                    title="View in Admin Users"
                    variant="secondary"
                  />
                </Link>
              </div>

              <IntelligencePersonaSelect
                className="w-full"
                disabled={isDeleted || updatingMemberId === member.userCompanyId}
                onPersonaChange={(persona) => void handlePersonaChange(member, persona)}
                onRemove={isDeleted ? undefined : () => setMemberToRemove(member)}
                persona={member.persona}
              />
            </div>
          );
        })}
      </div>

      <ConfirmDeleteModal
        isOpen={Boolean(memberToRemove)}
        itemName={`${memberToRemove?.fullName || memberToRemove?.email || 'member'} from this organization`}
        onConfirm={handleRemoveConfirm}
        setIsOpen={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      />
    </div>
  );
};
